import React, {useEffect, useState} from "react";
import {Box, Chip, IconButton, Typography} from "@mui/material";
import {grey} from "@mui/material/colors";
import Grid from '@mui/material/Grid';
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";

import {buildChildBiographicalData, buildTopTagsByCount} from "../../helpers/SessionHelper";
import {TAG_COLORS} from "../../helpers/MemberTagHelper";
import HistoricalSessionTooltip from "../common/HistoricalSessionTooltip";

const styles = {
    container: {
        minWidth: '12.5rem',
        padding: '0.25rem',
        textOverflow: 'ellipsis',
        overflowX: 'hidden',
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    headerItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
    },
    labelText: {
        color: '#000000',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    valueText: {
        color: '#000000',
        fontSize: '0.75rem',
    },
    iconButton: {
        padding: '0.125rem',
        marginLeft: '0.25rem'
    },
    copyIcon: {
        fontSize: '0.875rem'
    },
    historyBox: {
        alignItems: 'center',
        display: 'flex'
    },
    tagsSection: {
        borderTop: `0.0625rem solid ${grey[400]}`,
        paddingTop: '0.25rem',
    },
    tagsSectionTitle: {
        fontWeight: 600,
        fontSize: '0.75rem',
        marginBottom: '0.25rem',
    },
    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.25rem',
        marginBottom: '0.25rem',
    },
    tagLabelBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
    },
    tagChip: {
        height: '1.25rem',
        fontSize: '0.7rem',
        '& .MuiChip-label': {
            padding: '0 0.5rem',
        },
    },
    tagLabelText: {
        fontSize: '0.75rem'
    },
    voteChip: (sentiment) => ({
        backgroundColor: TAG_COLORS[sentiment],
        color: 'white',
        height: '1rem',
        fontSize: '0.75rem'
    }),
    expandIcon: {
        fontSize: '1rem'
    }
};

export default function ChildDetailsTooltip({child, serviceType, attendanceReport, tagMetrics = null, onOpenTagsDialog}) {
    const [topTagsByCount, setTopTagsByCount] = useState([]);

    useEffect(() => {
        if (tagMetrics !== null) {
            const tags = buildTopTagsByCount(tagMetrics);
            setTopTagsByCount(tags);
        }
    }, [tagMetrics]);

    const isLoadingTags = tagMetrics === null;
    const hasNoTags = tagMetrics !== null && topTagsByCount.length === 0;
    const hasTagsToDisplay = tagMetrics !== null && topTagsByCount.length > 0;


    const {age, region, memberId} = buildChildBiographicalData(child);

    const copyClicked = (id) => {
        navigator.clipboard.writeText(id);
    };

    return (
        <Box sx={styles.container}>
            <Grid container>
                <Grid size={12}>
                    <Box sx={styles.headerRow}>
                        <Box sx={styles.headerItem}>
                            <Typography sx={styles.labelText}>Age:</Typography>
                            <Typography sx={styles.valueText}>{age ?? '?'}</Typography>
                        </Box>
                        <Box sx={styles.headerItem}>
                            <Typography sx={styles.labelText}>Region:</Typography>
                            <Typography sx={styles.valueText}>{region}</Typography>
                        </Box>
                        <Box sx={styles.headerItem}>
                            <Typography sx={{...styles.labelText, mr: '0.4rem'}}>History</Typography>
                        </Box>
                    </Box>
                    <Box sx={{...styles.headerItem, justifyContent: 'space-between'}}>
                        <Box sx={styles.headerItem}>
                            <Typography sx={styles.labelText}>ID:</Typography>
                            <Typography sx={{...styles.valueText, marginLeft: '0.25rem'}}>{memberId}</Typography>
                            <IconButton onClick={() => copyClicked(memberId)} size="small" sx={styles.iconButton}>
                                <ContentCopyRounded sx={styles.copyIcon}/>
                            </IconButton>
                        </Box>
                        <Box sx={styles.historyBox}>
                            <HistoricalSessionTooltip {...{serviceType, attendanceReport, useHighContrast: true}} />
                        </Box>
                    </Box>
                </Grid>

                <Grid size={12} sx={styles.tagsSection}>
                    <Typography sx={styles.tagsSectionTitle}>Child Tags</Typography>
                    <Box sx={styles.tagsContainer}>
                        {
                            isLoadingTags
                            &&
                            <Typography sx={styles.valueText}>Loading tags...</Typography>
                        }
                        {
                            hasNoTags
                            &&
                            <Typography sx={styles.valueText}>No tags available</Typography>
                        }
                        {
                            hasTagsToDisplay
                            &&
                            topTagsByCount.map((tag) => (
                                <Chip
                                    key={tag.id}
                                    label={
                                        <Box sx={styles.tagLabelBox}>
                                            <Typography sx={{...styles.labelText, fontWeight: 'normal'}}>{tag.label}</Typography>
                                            {
                                                tag.votes > 0
                                                &&
                                                <Chip
                                                    label={tag.votes}
                                                    size="small"
                                                    sx={styles.voteChip(tag.sentiment)}
                                                />
                                            }
                                        </Box>
                                    }
                                    size="small"
                                    sx={styles.tagChip}
                                />
                            ))
                        }
                    </Box>
                    <Box sx={styles.headerItem}>
                        <Typography sx={styles.valueText}>View/Add Tags</Typography>
                        <IconButton size="small" sx={styles.iconButton} title="Pop-out window" onClick={onOpenTagsDialog}>
                            <AspectRatioIcon sx={styles.expandIcon}/>
                        </IconButton>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
